// ---------------------------------------------------------------------------
// Knowledge Base — relevance search (CLE-265).
//
// A small, dependency-free ranker that approximates "semantic" search by
// scoring documents on a weighted combination of title, tag, heading and body
// matches, plus a light synonym map so conceptually-related queries (e.g.
// "concediu" → "vacanță") still surface the right document. Good enough for a
// realistic demo without pulling an embeddings model into the frontend.
// ---------------------------------------------------------------------------

import { KB_DOCUMENTS, kbCategoryById, type KbDocument } from '../../testData/knowledgeBase';

export interface KbSearchHit {
  doc: KbDocument;
  score: number;
  /** A short snippet around the strongest body match, for the result list. */
  snippet: string;
  categoryLabel: string;
}

// Light synonym expansion — maps a query term to extra terms we also match on.
const SYNONYMS: Record<string, string[]> = {
  concediu: ['vacanță', 'odihnă', 'zile', 'liber'],
  deploy: ['livrare', 'producție', 'release', 'lansare'],
  review: ['revizuire', 'calitate', 'pr'],
  client: ['beneficiar', 'cont', 'onboarding'],
  api: ['endpoint', 'rest', 'serviciu'],
  bază: ['database', 'postgres', 'schema'],
  sla: ['suport', 'mentenanță', 'incident'],
  evaluare: ['performanță', 'feedback'],
  setup: ['instalare', 'mediu', 'environment'],
};

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function expandTerms(query: string): string[] {
  const base = normalize(query)
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  const expanded = new Set(base);
  for (const term of base) {
    for (const [key, syns] of Object.entries(SYNONYMS)) {
      if (normalize(key).includes(term) || term.includes(normalize(key))) {
        syns.forEach((s) => expanded.add(normalize(s)));
      }
    }
  }
  return [...expanded];
}

function buildSnippet(doc: KbDocument, terms: string[]): string {
  const plain = doc.content
    .replace(/[#>*`|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const lower = normalize(plain);
  let idx = -1;
  for (const t of terms) {
    const found = lower.indexOf(t);
    if (found !== -1 && (idx === -1 || found < idx)) idx = found;
  }
  if (idx === -1) return plain.slice(0, 140) + '…';
  const start = Math.max(0, idx - 60);
  const end = Math.min(plain.length, idx + 100);
  return (start > 0 ? '…' : '') + plain.slice(start, end).trim() + (end < plain.length ? '…' : '');
}

export interface KbSearchFilters {
  categoryId?: string;
  author?: string;
}

export function searchKb(query: string, filters: KbSearchFilters = {}): KbSearchHit[] {
  const terms = expandTerms(query);
  const trimmed = query.trim();

  const scored: KbSearchHit[] = [];

  for (const doc of KB_DOCUMENTS) {
    if (filters.categoryId && doc.categoryId !== filters.categoryId) continue;
    if (filters.author && doc.author !== filters.author) continue;

    let score = 0;
    if (trimmed) {
      const title = normalize(doc.title);
      const tags = doc.tags.map(normalize);
      const body = normalize(doc.content);

      for (const term of terms) {
        if (title.includes(term)) score += 10;
        if (tags.some((t) => t.includes(term))) score += 6;
        // Heading matches weigh more than plain body matches.
        const headingHit = doc.content
          .split('\n')
          .some((l) => /^#{1,4}\s/.test(l) && normalize(l).includes(term));
        if (headingHit) score += 4;
        const occurrences = body.split(term).length - 1;
        score += Math.min(occurrences, 5);
      }
    } else {
      // No query → list everything (used when only filters are active).
      score = 1;
    }

    if (score <= 0) continue;

    scored.push({
      doc,
      score,
      snippet: buildSnippet(doc, terms),
      categoryLabel: kbCategoryById(doc.categoryId)?.label ?? doc.categoryId,
    });
  }

  return scored.sort((a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title));
}
